// The close screen's state, as a URL search param.
//
// The month is the ONE thing this screen is, and until it lived here it lived
// in component state — so `/billing/close` meant "last month" to whoever opened
// it and something else to whoever they sent it to. "Have a look at August"
// is the most ordinary sentence anybody says about a close, and it has to be a
// link. Back walks the months too, which stepping through component state
// silently swallowed.
//
// It follows the hardened shape `calendarSearch.ts` settled on, for the reason
// set out there at length: the field carries its own `.catch(undefined)` so an
// unreadable param falls back rather than 404s, and the parse runs AGAIN in
// `searchToPeriod` at the point of use, because TanStack merges each route's
// validated search over its parent's and the root route validates nothing — so
// a `?period=2026-13` this schema rejected still reaches the component, and
// without the second parse it would reach the API, where it is a 400 on a
// screen that should have shown last month.

import { z } from "zod";
import {
  addMonths,
  formatIsoMonth,
  parseCalendarMonth,
  todayIsoDate,
} from "../../format/calendarDate.js";

const IsoMonthPattern = /^\d{4}-\d{2}$/;

const CloseSearchSchema = z.object({
  /** `YYYY-MM` — the month being closed. Absent means the month just gone. */
  period: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  /**
   * The deal a link came for — the runway's "Bill it" names one. Its card is
   * scrolled to and marked, and nothing is filtered: the rest of the month is
   * still the month.
   */
  deal: z.string().uuid().optional().catch(undefined),
});

export type CloseSearch = z.infer<typeof CloseSearchSchema>;

export function validateCloseSearch(raw: Record<string, unknown>): CloseSearch {
  const parsed = CloseSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/**
 * The month the page opens on: the one just gone.
 *
 * Not the current month, which is not over — asking what a month owes while you
 * are still in it means chasing figures for days that have not happened. The
 * first thing somebody does on the 1st of October is bill September.
 */
export function defaultPeriod(today: string = todayIsoDate()): string {
  const month = parseCalendarMonth(today.slice(0, 7));
  if (month === null) return today.slice(0, 7);
  return formatIsoMonth(addMonths(month, -1));
}

/** The last day of a month that still opens the To bill screen on the month before. */
export const CLOSE_LAST_DAY_FOR_PREVIOUS_MONTH = 10;

/**
 * The month the To bill screen opens on.
 *
 * Up to the 10th, the month just gone, which is still being billed. From the
 * 11th, the current month: last month is billed by then, and this month is the
 * one being worked on. So October 1–10 opens on September and October 11 opens
 * on October.
 */
export function closeDefaultPeriod(today: string = todayIsoDate()): string {
  const month = parseCalendarMonth(today.slice(0, 7));
  if (month === null) return today.slice(0, 7);
  const day = Number(today.slice(8, 10));
  const offset = day <= CLOSE_LAST_DAY_FOR_PREVIOUS_MONTH ? -1 : 0;
  return formatIsoMonth(addMonths(month, offset));
}

/** The month a screen opens on when its URL names none. */
export type DefaultPeriod = (today: string) => string;

/** The month this URL is asking for, or the default when it does not say. */
export function searchToPeriod(
  raw: CloseSearch,
  today: string = todayIsoDate(),
  fallback: DefaultPeriod = defaultPeriod,
): string {
  const search = validateCloseSearch(raw as Record<string, unknown>);
  const month = search.period === undefined ? null : parseCalendarMonth(search.period);
  return month === null ? fallback(today) : formatIsoMonth(month);
}

/**
 * The default is omitted, so the month somebody opens the screen on is a clean
 * URL — a link that keeps following the default tomorrow, which is the right
 * thing for the sidebar's own entry to mean.
 */
export function periodToSearch(
  period: string,
  today: string = todayIsoDate(),
  fallback: DefaultPeriod = defaultPeriod,
): CloseSearch {
  return period === fallback(today) ? {} : { period };
}

/** The deal this URL was opened for, if it names a readable one. */
export function searchToDeal(raw: CloseSearch): string | null {
  return validateCloseSearch(raw as Record<string, unknown>).deal ?? null;
}

/** Step the close a month at a time. */
export function shiftPeriod(period: string, delta: number): string {
  const month = parseCalendarMonth(period);
  if (month === null) return period;
  return formatIsoMonth(addMonths(month, delta));
}
